import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ITransaction } from '../../../../Utils/_models/payment-transactions.model';
import { ConfirmService } from '../../../../Utils/_services/confirmation-dialog.service';
import { PaymentTransactionsService } from '../../../../Utils/_services/payment/payment.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-transaction-details-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './transaction-details-modal.component.html',
  styleUrl: './transaction-details-modal.component.css',
})
export class TransactionDetailsModalComponent implements OnInit {
  @Input() visible = false;
  @Input() transaction!: ITransaction;
  @Output() onClose = new EventEmitter<void>();
  @Output() refunded = new EventEmitter<void>();

  constructor(
    private confirmService: ConfirmService,
    private paymentService: PaymentTransactionsService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void { }

  close(): void {
    this.onClose.emit();
  }

  get isRefundDisabled(): boolean {
    if (!this.transaction) return true;
    if (this.transaction.type === 'refund' || this.transaction.status === 'refunded' || this.transaction.description?.toLowerCase().includes('refund')) return true;
    if (!this.transaction.createdAt) return true;
    const createdAtTime = new Date(this.transaction.createdAt).getTime();
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    const diff = Date.now() - createdAtTime;
    return diff > ninetyDaysMs;
  }

  async onRefund() {
    const confirmed = await this.confirmService.confirm({
      header: 'Refund Transaction',
      bodyHeader: 'Are you sure you want to refund this transaction?',
      bodySubtitle: 'This action cannot be undone.',
      yesButtonText: 'Refund',
      noButtonText: 'Cancel',
      yesButtonColor: '#dc2626',
      noButtonColor: '#6b7280'
    });

    if (confirmed) {
      this.paymentService.refundTransaction(this.transaction._id).subscribe({
        next: (res) => {
          this.toastr.success(res?.message || 'Transaction refunded successfully');
          this.refunded.emit();
          this.close();
        },
        error: (err) => {
          this.toastr.error(err?.msg || err?.message || 'Failed to refund transaction');
        }
      });
    }
  }

  getDisplayName(): string {
    if (!this.transaction?.userInfo) return 'Unknown';
    const name = `${this.transaction.userInfo.firstName ?? ''} ${this.transaction.userInfo.lastName ?? ''}`.trim();
    return name || this.transaction.userInfo.email?.split('@')[0] || 'Unknown';
  }

  getInitials(): string {
    if (!this.transaction?.userInfo) return '??';
    const f = this.transaction.userInfo.firstName?.[0] ?? '';
    const l = this.transaction.userInfo.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '??';
  }

  resolvePaymentType(): { label: string; type: 'card' | 'paypal' | 'bank' } {
    const desc = (this.transaction?.description ?? '').toLowerCase();
    if (this.transaction?.type === 'subscription' || desc.includes('subscription'))
      return { label: 'Credit Card', type: 'card' };
    if (desc.includes('stripe') || desc.includes('card'))
      return { label: 'Credit Card', type: 'card' };
    if (desc.includes('paypal'))
      return { label: 'PayPal', type: 'paypal' };
    if (desc.includes('bank') || desc.includes('transfer'))
      return { label: 'Bank Transfer', type: 'bank' };
    return { label: 'Wallet Top-up', type: 'card' };
  }
}
